import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API } from "../App";
import { Sidebar } from "../components/Sidebar";
import { LeaveRequestForm } from "../components/LeaveRequestForm";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  Calendar, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Hourglass,
  Palmtree,
  Trash2,
  Users,
  CalendarDays,
  Menu,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Send
} from "lucide-react";
import { format, isWithinInterval, parseISO, eachDayOfInterval, startOfMonth, endOfMonth, addMonths, isWeekend } from "date-fns";
import { fr } from "date-fns/locale";

export default function CollaborateurDashboard() {
  const [balance, setBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [colleaguesLeaves, setColleaguesLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [reportMessage, setReportMessage] = useState("");
  const [reportLoading, setReportLoading] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  
  const { getAuthHeader, user } = useAuth();

  const fetchData = async () => {
    try {
      const [balanceRes, requestsRes, colleaguesRes] = await Promise.all([
        axios.get(`${API}/leave-balance`, getAuthHeader()),
        axios.get(`${API}/leave-requests`, getAuthHeader()),
        axios.get(`${API}/leave-requests/colleagues`, getAuthHeader())
      ]);
      setBalance(balanceRes.data);
      setRequests(requestsRes.data);
      setColleaguesLeaves(colleaguesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCancelRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/leave-requests/${requestId}`, getAuthHeader());
      toast.success("Demande annulée");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'annulation");
    }
  };

  const handleReportError = async () => {
    if (!reportMessage.trim()) return;
    setReportLoading(true);
    try {
      await axios.post(`${API}/balance/report-error`, { message: reportMessage }, getAuthHeader());
      toast.success("Signalement envoyé au service RH");
      setReportMessage("");
      setReportOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setReportLoading(false);
    }
  };

  const getStatusBadge = (status, compact = false) => {
    const styles = {
      pending: { class: "bg-amber-50 text-amber-700 border-amber-200", icon: Hourglass, label: "En attente" },
      approved: { class: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, label: "Approuvé" },
      rejected: { class: "bg-red-50 text-red-700 border-red-200", icon: XCircle, label: "Refusé" }
    };
    const style = styles[status] || styles.pending;
    return (
      <Badge variant="outline" className={`${style.class} border ${compact ? 'text-xs px-2 py-0.5' : ''}`}>
        <style.icon className={`${compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} mr-1`} />
        {compact ? style.label.substring(0, 3) : style.label}
      </Badge>
    );
  };

  // Mini calendar for team view
  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  const getColleaguesOnLeave = (day) => {
    return colleaguesLeaves.filter(leave => {
      const start = parseISO(leave.start_date);
      const end = parseISO(leave.end_date);
      return isWithinInterval(day, { start, end });
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <div className="hidden lg:block">
          <Sidebar />
        </div>
        <main className="flex-1 bg-gray-50 p-4 lg:p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3].map(i => <div key={i} className="h-24 lg:h-32 bg-gray-200 rounded-lg"></div>)}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {/* Mobile Sidebar */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <Sidebar />
        </SheetContent>
      </Sheet>

      <main className="flex-1 bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-4 lg:px-8 py-4 lg:py-6 sticky top-0 z-40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-lg lg:text-2xl font-bold text-[#1a2634]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Mon espace congés
                </h1>
                <p className="text-gray-500 text-sm hidden sm:block">Gérez vos demandes et consultez votre solde</p>
              </div>
            </div>
            <LeaveRequestForm onSuccess={fetchData} />
          </div>
        </header>

        <div className="p-4 lg:p-8">
          {/* Balance cards */}
          <div className="grid grid-cols-2 gap-2 lg:gap-6 mb-6 lg:mb-8">
            <Card className="stat-card border-0 shadow-sm" data-testid="cp-remaining-card">
              <CardContent className="p-3 lg:pt-6 lg:p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                  <div className="text-center lg:text-left">
                    <p className="text-[10px] lg:text-sm font-medium text-gray-500 uppercase tracking-wide">CP Dispo</p>
                    <p className="text-2xl lg:text-4xl font-black text-[#1a2634] mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {balance?.cp_remaining?.toFixed(1) || 0}
                    </p>
                    <p className="text-[10px] lg:text-xs text-gray-400 mt-0.5 hidden sm:block">jours restants</p>
                  </div>
                  <div className="hidden lg:block p-3 bg-[#00A3E0]/10 rounded-lg">
                    <Palmtree className="h-6 w-6 text-[#00A3E0]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card border-0 shadow-sm" data-testid="cp-acquiring-card">
              <CardContent className="p-3 lg:pt-6 lg:p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                  <div className="text-center lg:text-left">
                    <p className="text-[10px] lg:text-sm font-medium text-gray-500 uppercase tracking-wide">En cours</p>
                    <p className="text-2xl lg:text-4xl font-black text-emerald-600 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {balance?.cp_acquiring?.toFixed(1) || 0}
                    </p>
                    <p className="text-[10px] lg:text-xs text-gray-400 mt-0.5 hidden sm:block">acquisition</p>
                  </div>
                  <div className="hidden lg:block p-3 bg-emerald-50 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Détail des soldes CP */}
          {(balance?.cp_n1 > 0 || balance?.cp_n2 > 0 || balance?.cp_anciennete > 0) && (
            <Card className="border-0 shadow-sm mb-6 lg:mb-8">
              <CardHeader className="border-b border-gray-100 py-3 lg:pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm lg:text-base font-semibold text-gray-700">Détail des CP</CardTitle>
                  <Dialog open={reportOpen} onOpenChange={setReportOpen}>
                    <DialogTrigger asChild>
                      <button
                        className="group flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-white text-gray-400 text-[11px] lg:text-xs transition-all hover:border-red-300 hover:bg-red-50 hover:text-red-500 hover:shadow-sm"
                        data-testid="report-error-btn"
                      >
                        <AlertCircle className="h-3 w-3 transition-transform group-hover:scale-110" />
                        <span className="hidden sm:inline">Signaler une erreur</span>
                        <span className="sm:hidden">Erreur</span>
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-[90vw] lg:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Signaler une erreur de solde</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3 py-2">
                        <p className="text-sm text-gray-500">Décrivez l'erreur constatée sur votre solde de congés. Le service RH sera notifié.</p>
                        <Textarea
                          placeholder="Ex: Mon solde N-2 devrait être de 5 jours au lieu de 0..."
                          value={reportMessage}
                          onChange={(e) => setReportMessage(e.target.value)}
                          rows={3}
                          data-testid="report-error-message"
                        />
                      </div>
                      <DialogFooter>
                        <Button
                          onClick={handleReportError}
                          disabled={reportLoading || !reportMessage.trim()}
                          className="bg-[#E63946] hover:bg-[#c5303c] text-white"
                          data-testid="report-error-submit"
                        >
                          <Send className="h-3.5 w-3.5 mr-1.5" />
                          {reportLoading ? "Envoi..." : "Envoyer au RH"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent className="p-3 lg:pt-4">
                <div className="grid grid-cols-3 gap-2 lg:gap-4">
                  {balance?.cp_n1 > 0 && (
                    <div className="p-2 lg:p-4 bg-gray-50 rounded-lg text-center lg:text-left">
                      <p className="text-[10px] lg:text-sm text-gray-500">N-1</p>
                      <p className="text-lg lg:text-2xl font-bold text-[#1a2634]">
                        {(balance.cp_n1 - balance.cp_n1_taken).toFixed(1)}
                      </p>
                      <p className="text-[10px] text-gray-400">/ {balance.cp_n1}</p>
                    </div>
                  )}
                  {balance?.cp_n2 > 0 && (
                    <div className="p-2 lg:p-4 bg-gray-50 rounded-lg text-center lg:text-left">
                      <p className="text-[10px] lg:text-sm text-gray-500">N-2</p>
                      <p className="text-lg lg:text-2xl font-bold text-[#1a2634]">
                        {(balance.cp_n2 - balance.cp_n2_taken).toFixed(1)}
                      </p>
                      <p className="text-[10px] text-gray-400">/ {balance.cp_n2}</p>
                    </div>
                  )}
                  {balance?.cp_anciennete > 0 && (
                    <div className="p-2 lg:p-4 bg-gray-50 rounded-lg text-center lg:text-left">
                      <p className="text-[10px] lg:text-sm text-gray-500">Ancienneté</p>
                      <p className="text-lg lg:text-2xl font-bold text-[#1a2634]">
                        {(balance.cp_anciennete - balance.cp_anciennete_taken).toFixed(1)}
                      </p>
                      <p className="text-[10px] text-gray-400">/ {balance.cp_anciennete}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Service leaves timeline - Full width */}
          <div className="mb-6 lg:mb-8">
            <Card className="border-0 shadow-sm">
              <CardHeader className="border-b border-gray-100 py-3 lg:py-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm lg:text-lg font-semibold text-[#1a2634] flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    <Users className="h-4 w-4 lg:h-5 lg:w-5 text-[#00A3E0]" />
                    Congés du service
                  </CardTitle>
                  <div className="flex items-center gap-1 lg:gap-2">
                    <Button variant="ghost" size="icon" className="h-7 w-7 lg:h-8 lg:w-8" onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs lg:text-sm font-medium text-gray-700 min-w-[100px] text-center capitalize">
                      {format(currentMonth, "MMMM yyyy", { locale: fr })}
                    </span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 lg:h-8 lg:w-8" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-3 lg:p-4 overflow-x-auto">
                {colleaguesLeaves.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 lg:py-12 text-gray-400">
                    <CalendarDays className="h-8 w-8 lg:h-10 lg:w-10 mb-2 lg:mb-3" />
                    <p className="text-xs lg:text-sm text-center">Aucun congé prévu dans votre service</p>
                  </div>
                ) : (() => {
                  const monthStart = startOfMonth(currentMonth);
                  const monthEnd = endOfMonth(currentMonth);
                  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
                  
                  const monthLeaves = colleaguesLeaves.filter(l => {
                    const s = parseISO(l.start_date);
                    const e = parseISO(l.end_date);
                    return s <= monthEnd && e >= monthStart;
                  });
                  
                  const userMap = {};
                  monthLeaves.forEach(l => {
                    if (!userMap[l.user_id]) userMap[l.user_id] = { name: l.user_name, leaves: [] };
                    userMap[l.user_id].leaves.push(l);
                  });
                  const users = Object.values(userMap);

                  if (users.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                        <CalendarDays className="h-8 w-8 mb-2" />
                        <p className="text-xs lg:text-sm">Aucune absence ce mois-ci</p>
                      </div>
                    );
                  }

                  const isToday_ = (d) => {
                    const t = new Date();
                    return d.getDate() === t.getDate() && d.getMonth() === t.getMonth() && d.getFullYear() === t.getFullYear();
                  };

                  return (
                    <div className="space-y-1 min-w-[500px]">
                      {/* Day headers */}
                      <div className="flex items-center">
                        <div className="w-24 lg:w-32 flex-shrink-0" />
                        <div className="flex-1 flex">
                          {days.map((day, i) => (
                            <div key={i} className={`flex-1 text-center text-[9px] lg:text-[11px] py-0.5 ${isWeekend(day) ? 'text-gray-300' : isToday_(day) ? 'text-[#00A3E0] font-bold' : 'text-gray-400'}`}>
                              {format(day, 'd')}
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* User rows */}
                      {users.map((u, idx) => (
                        <div key={idx} className="flex items-center">
                          <div className="w-24 lg:w-32 flex-shrink-0 flex items-center gap-1.5 pr-2">
                            <div className="w-6 h-6 rounded-full bg-[#00A3E0] text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                              {u.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </div>
                            <span className="text-xs text-gray-700 truncate">{u.name.split(' ')[0]}</span>
                          </div>
                          <div className="flex-1 flex h-7 gap-px">
                            {days.map((day, i) => {
                              const inLeave = u.leaves.some(l => isWithinInterval(day, { start: parseISO(l.start_date), end: parseISO(l.end_date) }));
                              return (
                                <div key={i} className={`flex-1 rounded-sm transition-all ${isWeekend(day) ? 'bg-gray-100' : inLeave ? 'bg-[#00A3E0]' : 'bg-gray-50'}`} />
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      {/* Legend */}
                      <div className="flex items-center gap-4 pt-2 border-t border-gray-100 mt-1">
                        <div className="flex items-center gap-1.5">
                          <div className="w-3 h-3 rounded-sm bg-[#00A3E0]" />
                          <span className="text-[10px] text-gray-500">En congé</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-3 h-3 rounded-sm bg-gray-100" />
                          <span className="text-[10px] text-gray-500">Week-end</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>

          {/* My requests - Full width */}
          <div>
            <Card className="border-0 shadow-sm">
              <CardHeader className="border-b border-gray-100 py-3 lg:py-4">
                <CardTitle className="text-sm lg:text-lg font-semibold text-[#1a2634]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Mes demandes
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[300px] lg:h-[450px]">
                  {requests.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 lg:py-16 text-gray-400">
                      <Calendar className="h-10 w-10 lg:h-12 lg:w-12 mb-3 lg:mb-4" />
                      <p className="text-sm">Aucune demande</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {requests.map((request, index) => (
                        <div 
                          key={request.id} 
                          className="p-3 lg:p-4 hover:bg-gray-50 transition-colors animate-fade-in"
                          style={{ animationDelay: `${index * 50}ms` }}
                          data-testid={`leave-request-${request.id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2 lg:gap-4 flex-1 min-w-0">
                              <div className={`w-1 h-10 lg:h-12 rounded-full flex-shrink-0 ${
                                request.status === 'approved' ? 'bg-emerald-500' :
                                request.status === 'rejected' ? 'bg-red-500' : 'bg-amber-500'
                              }`} />
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-[#1a2634] text-sm lg:text-base truncate">{request.leave_type_name}</p>
                                <div className="flex flex-wrap items-center gap-1 lg:gap-2 text-xs lg:text-sm text-gray-500 mt-0.5 lg:mt-1">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3 lg:h-4 lg:w-4 flex-shrink-0" />
                                    <span className="truncate">
                                      {format(new Date(request.start_date), "dd/MM", { locale: fr })} - {format(new Date(request.end_date), "dd/MM/yy", { locale: fr })}
                                    </span>
                                  </span>
                                  <span className="text-gray-300 hidden sm:inline">•</span>
                                  <span className="text-[10px] lg:text-xs">{request.days_count}j</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 lg:gap-3 flex-shrink-0">
                              <span className="hidden sm:inline">{getStatusBadge(request.status)}</span>
                              <span className="sm:hidden">{getStatusBadge(request.status, true)}</span>
                              {request.status === 'pending' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 lg:h-8 lg:w-8"
                                  onClick={() => handleCancelRequest(request.id)}
                                  data-testid={`cancel-request-${request.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-gray-400 hover:text-red-600" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {request.comment && (
                            <p className="text-[10px] lg:text-xs text-gray-400 mt-1.5 lg:mt-2 ml-3 italic truncate">"{request.comment}"</p>
                          )}
                          {request.review_comment && (
                            <div className="mt-2 lg:mt-3 ml-3 p-2 lg:p-3 bg-gray-50 rounded-lg text-[10px] lg:text-sm text-gray-600">
                              <strong>Manager:</strong> {request.review_comment}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
